import React, { useState } from 'react';
import { Layers, Download, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика дивидендных выплат
 */
const PaymentSchedule = ({ schedule, results, showFullSchedule, setShowFullSchedule }) => {
  const [activeTab, setActiveTab] = useState('general');
  
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первого года, если не показан полный график
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, Math.min(12, schedule.length));

  // Получаем символ валюты
  const currencySymbol = results.currencySymbol || '₽';

  // Начальные итоговые суммы
  const totals = {
    gross: schedule.reduce((sum, item) => sum + item.grossDividend, 0),
    tax: schedule.reduce((sum, item) => sum + item.tax, 0),
    net: schedule.reduce((sum, item) => sum + item.netDividend, 0),
    reinvested: schedule.reduce((sum, item) => sum + (item.reinvestedAmount || 0), 0)
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-green-500" />
          График дивидендных выплат ({schedule.length} периодов)
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 12 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${schedule.length} периодов`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Основные показатели
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'detailed'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Детальный график
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showFullSchedule 
            ? "Полный график всех дивидендных выплат за весь период прогнозирования. Прокрутите таблицу вправо для просмотра всех данных." 
            : `Показаны первые ${displaySchedule.length} периодов выплат. Нажмите "Показать все периоды" для просмотра полного графика.`
          }</p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] payment-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">№</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Количество акций</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Дивиденд до налогов</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Налоги</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Дивиденд после налогов</th>
                  {results.reinvestmentRate > 0 && (
                    <th scope="col" className="px-3 py-2 font-semibold text-right">Реинвестировано</th>
                  )}
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Кол-во акций</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Дивиденд на акцию</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Дивиденд до налогов</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Налоги</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Дивиденд после налогов</th>
                  {results.reinvestmentRate > 0 && (
                    <th scope="col" className="px-3 py-2 font-semibold text-right">Реинвестировано</th>
                  )}
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Цена акции</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Стоимость портфеля</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displaySchedule.map((item) => (
              <tr
                key={item.period}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
              >
                <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.period}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </td>

                {activeTab === 'general' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatNumber(item.sharesCount, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">{currencySymbol} {formatNumber(item.grossDividend, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">{currencySymbol} {formatNumber(item.tax, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-green-600 dark:text-green-400 font-semibold">{currencySymbol} {formatNumber(item.netDividend, 2)}</td>
                    {results.reinvestmentRate > 0 && (
                      <td className="px-3 py-1.5 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">
                        {item.reinvestedAmount > 0 ? `${currencySymbol} ${formatNumber(item.reinvestedAmount, 2)}` : '-'}
                      </td>
                    )}
                  </>
                )}

                {activeTab === 'detailed' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatNumber(item.sharesCount, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{currencySymbol} {formatNumber(item.dividendPerShare, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{currencySymbol} {formatNumber(item.grossDividend, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">{currencySymbol} {formatNumber(item.tax, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-green-600 dark:text-green-400">{currencySymbol} {formatNumber(item.netDividend, 2)}</td>
                    {results.reinvestmentRate > 0 && (
                      <td className="px-3 py-1.5 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">
                        {item.reinvestedAmount > 0 ? `${currencySymbol} ${formatNumber(item.reinvestedAmount, 2)}` : '-'}
                      </td>
                    )}
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{currencySymbol} {formatNumber(item.stockPrice, 2)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">{currencySymbol} {formatNumber(item.portfolioValue, 2)}</td>
                  </>
                )}
              </tr>
            ))}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="2">Итого:</td>

              {activeTab === 'general' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(results.finalSharesCount, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{currencySymbol} {formatNumber(totals.gross, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-red-600 dark:text-red-400">{currencySymbol} {formatNumber(totals.tax, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">{currencySymbol} {formatNumber(totals.net, 2)}</td>
                  {results.reinvestmentRate > 0 && (
                    <td className="px-3 py-2 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{currencySymbol} {formatNumber(totals.reinvested, 2)}</td>
                  )}
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(results.finalSharesCount, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{currencySymbol} {formatNumber(totals.gross, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-red-600 dark:text-red-400">{currencySymbol} {formatNumber(totals.tax, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">{currencySymbol} {formatNumber(totals.net, 2)}</td>
                  {results.reinvestmentRate > 0 && (
                    <td className="px-3 py-2 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{currencySymbol} {formatNumber(totals.reinvested, 2)}</td>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap text-right">{currencySymbol} {formatNumber(results.finalStockPrice, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{currencySymbol} {formatNumber(results.finalPortfolioValue, 2)}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > 12 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} периодов выплат
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentSchedule;